import { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  CheckCircle,
  Shield,
  Droplets,
  Wrench
} from 'lucide-react';

const HydroJettingPricing = () => {

  useEffect(() => {
    // SEO Meta Tags
    
    const metaTags = [
      { 
        name: 'description', 
        content: 'Hydro jetting pricing Denver Metro. See actual costs: Mainline jetting $695, Branch line jetting $695. High-pressure drain cleaning with transparent pricing. Licensed, insured, master plumber owned.' 
      },
      { 
        name: 'keywords', 
        content: 'hydro jetting cost Denver, high pressure drain cleaning price Colorado, hydro jetting service cost, drain cleaning pricing' 
      },
      { 
        property: 'og:title', 
        content: 'Hydro Jetting Pricing | Denver Colorado | $695 | Colorado First' 
      },
      { 
        property: 'og:description', 
        content: 'See actual hydro jetting costs before we arrive. Mainline $695, Branch lines $695. High-pressure water cleaning with photo proof.' 
      },
      { 
        property: 'og:type', 
        content: 'website' 
      },
      { 
        property: 'og:url', 
        content: 'https://colorado-plumbing.com/hydro-jetting-pricing' 
      },
    ];
    
    metaTags.forEach(tag => {
      let metaElement = document.querySelector(`meta[name="${tag.name}"]`) || 
                      document.querySelector(`meta[property="${tag.property}"]`);
      
      if (!metaElement) {
        metaElement = document.createElement('meta');
        if (tag.name) metaElement.setAttribute('name', tag.name);
        if (tag.property) metaElement.setAttribute('property', tag.property);
        document.head.appendChild(metaElement);
      }
      
      metaElement.setAttribute('content', tag.content);
    });

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      canonicalLink.setAttribute('href', 'https://colorado-plumbing.com/hydro-jetting-pricing');
      document.head.appendChild(canonicalLink);
    }

  }, []);


  return (
    <div className="min-h-screen bg-neutral-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-48 pb-16 bg-gradient-to-br from-action-primary to-action-urgent">
        <div className="container mx-auto px-4 text-center relative z-10 py-16 md:py-24">
          <div className="inline-block px-6 py-3 mb-8 rounded-full bg-success-primary/90 text-white font-bold text-lg">
            💧 High-Pressure Cleaning • Grease & Root Removal • 4000 PSI Power
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold mb-6 text-neutral-white leading-tight">
            Hydro Jetting Pricing
            <span className="block text-2xl md:text-3xl text-action-warm/90 font-semibold mt-2">
              High-Pressure Drain & Sewer Cleaning
            </span>
          </h1>
          
          <p className="text-lg text-neutral-white/90 mb-8">
            Master Plumber Owned • 7+ Years Denver Metro • 293+ Reviews • Professional High-Pressure Equipment
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            
            <a 
              href="#hydro-jetting-pricing-details" 
              className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-10 py-5 rounded-full text-xl font-semibold inline-block transition-all duration-300"
            >
              💰 See All Pricing Below
            </a>
          </div>
        </div>
      </section>

      {/* Detailed Pricing Section */}
      <section id="hydro-jetting-pricing-details" className="py-20 bg-neutral-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-authority-navy">
              Transparent Hydro Jetting Pricing
            </h2>
            <p className="text-xl text-neutral-gray mb-4">
              See actual high-pressure drain cleaning costs with no hidden fees or surprise charges
            </p>
            <div className="inline-flex items-center bg-success-light px-6 py-3 rounded-full border-2 border-success-primary/20">
              <Shield className="w-6 h-6 text-success-primary mr-3" />
              <span className="text-success-primary font-bold">15+ Hydro Jetting Services • All Pricing Online</span>
            </div>
          </div>

          {/* Mainline Hydro Jetting Services */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-action-warm/10 to-action-primary/10 rounded-2xl p-8 border-2 border-action-primary/20 mb-8">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-action-primary to-action-urgent rounded-full flex items-center justify-center mr-6 shadow-lg">
                  <Droplets size={32} className="text-neutral-white" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-authority-navy mb-2">Mainline Hydro Jetting Services</h3>
                  <p className="text-lg text-neutral-gray">High-pressure cleaning for main sewer lines - removes grease, roots, and heavy blockages</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Mainline Pricing Table */}
                <div>
                  <div className="bg-neutral-white rounded-xl shadow-lg border border-action-primary/10 overflow-hidden">
                    <div className="bg-gradient-to-r from-action-primary to-action-urgent text-white p-4">
                      <h4 className="text-xl font-bold text-center">Mainline Hydro Jetting Pricing</h4>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                        <span className="font-semibold text-authority-navy">Hydro-Jetting from Outside Cleanout</span>
                        <span className="text-xl font-bold text-success-primary">$695</span>
                      </div>
                    </div>
                    <div className="bg-success-light p-4">
                      <h5 className="font-bold text-success-primary mb-3">Mainline Jetting Advantages</h5>
                      <ul className="space-y-2 text-sm text-success-primary">
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          High-pressure water removes grease & roots
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Long Reach (100ft reach)
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Before/after camera look included
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          More effective than traditional snaking
                        </li>
                      </ul>
                    </div>
                    
                    {/* Exclusions Section */}
                    <div className="bg-yellow-50 p-4 border-t border-yellow-200">
                      <p className="text-yellow-800 font-semibold text-sm mb-2">*Mainline Jetting Exclusions:</p>
                      <ul className="text-yellow-700 text-xs space-y-1">
                        <li>• Full camera inspection with recording (available separately)</li>
                        <li>• Heavy cast iron descaling (see descaling service)</li>
                        <li>• Sewer line repair if damage found</li>
                        <li>• Cleanout installation or repair</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Mainline Photo Gallery */}
                <div>
                  <div className="bg-neutral-white rounded-xl shadow-lg border border-action-primary/10 overflow-hidden">
                    <div className="aspect-video relative">
                      <img
                        src="/colorado-plumbing-uploads/services/sewer-services/camera-scope-sewer-arvada.JPEG"
                        alt="Camera scope sewer inspection in Arvada Colorado - Colorado First Plumbing"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-authority-navy/60 to-transparent"></div>
                      <div className="absolute bottom-4 left-4 right-4 text-white">
                        <h4 className="text-lg font-bold mb-1">
                          Camera Scope Sewer Inspection - Arvada
                        </h4>
                        <p className="text-sm text-trust-light">
                          Professional sewer camera inspection before hydro jetting service
                        </p>
                      </div>
                    </div>
                    <div className="p-6">
                      <h4 className="font-bold text-authority-navy mb-2">Professional Equipment Features</h4>
                      <ul className="space-y-2 text-sm text-neutral-gray">
                        <li className="flex items-center">
                          <CheckCircle size={16} className="text-success-primary mr-2" />
                          4000 PSI high-pressure water system
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="text-success-primary mr-2" />
                          Specialized jetting heads for different blockages
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="text-success-primary mr-2" />
                          Safe for all pipe materials when properly operated
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="text-success-primary mr-2" />
                          Real-time monitoring and control systems
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Branch Line Hydro Jetting Services */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-trust-light to-success-light/30 rounded-2xl p-8 border-2 border-trust-primary/20 mb-8">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-trust-primary to-trust-deep rounded-full flex items-center justify-center mr-6 shadow-lg">
                  <Wrench size={32} className="text-neutral-white" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-authority-navy mb-2">Branch Line Hydro Jetting Services</h3>
                  <p className="text-lg text-neutral-gray">Targeted high-pressure cleaning for kitchen, bathroom, and utility drain lines</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Branch Line Pricing Table */}
                <div>
                  <div className="bg-neutral-white rounded-xl shadow-lg border border-trust-primary/10 overflow-hidden">
                    <div className="bg-gradient-to-r from-trust-primary to-trust-deep text-white p-4">
                      <h4 className="text-xl font-bold text-center">Branch Line Hydro Jetting Pricing</h4>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                        <span className="font-semibold text-authority-navy">Kitchen Grease Remover Descale and Flush</span>
                        <span className="text-xl font-bold text-success-primary">$495</span>
                      </div>
                    </div>
                    <div className="bg-success-light p-4">
                      <h5 className="font-bold text-success-primary mb-3">Kitchen Branch Line Milwaukee Grease Descale Benefits</h5>
                      <ul className="space-y-2 text-sm text-success-primary">
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Deep cleaning removes soap scum & biofilm
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          More effective than snaking for grease
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Typically reaches 50ft in branch lines
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Prevents recurring blockages longer
                        </li>
                      </ul>
                    </div>
                    
                    {/* Exclusions Section */}
                    <div className="bg-yellow-50 p-4 border-t border-yellow-200">
                      <p className="text-yellow-800 font-semibold text-sm mb-2">*Branch Line Jetting Exclusions:</p>
                      <ul className="text-yellow-700 text-xs space-y-1">
                        <li>• Mainline jetting (see mainline pricing above)</li>
                        <li>• Camera inspection (available separately)</li>
                        <li>• Drain line repair or replacement</li>
                        <li>• P-trap removal and cleaning (manual service available)</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Branch Line Photo Gallery */}
                <div>
                  <div className="bg-neutral-white rounded-xl shadow-lg border border-trust-primary/10 overflow-hidden">
                    <div className="aspect-video relative">
                      <img
                        src="/colorado-plumbing-uploads/services/sewer-services/sewer-camera-verify-repair-littleton.JPEG"
                        alt="Sewer camera verify repair in Littleton Colorado - Colorado First Plumbing"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-authority-navy/60 to-transparent"></div>
                      <div className="absolute bottom-4 left-4 right-4 text-white">
                        <h4 className="text-lg font-bold mb-1">
                          Sewer Camera Verify Repair - Littleton
                        </h4>
                        <p className="text-sm text-trust-light">
                          Camera verification after hydro jetting service completion
                        </p>
                      </div>
                    </div>
                    <div className="p-6">
                      <h4 className="font-bold text-authority-navy mb-2">Specialized Branch Line Services</h4>
                      <ul className="space-y-2 text-sm text-neutral-gray">
                        <li className="flex items-center">
                          <CheckCircle size={16} className="text-success-primary mr-2" />
                          Kitchen lines: Grease and food particle removal
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="text-success-primary mr-2" />
                          Bathroom lines: Hair and soap scum elimination
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="text-success-primary mr-2" />
                          Laundry lines: Lint and detergent buildup cleaning
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="text-success-primary mr-2" />
                          Safe pressure settings for all pipe types
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Final CTA Section */}
          <div className="text-center">
            <div className="bg-gradient-to-r from-trust-deep to-authority-navy rounded-2xl p-8 text-white shadow-2xl border border-trust-primary/20">
              <h3 className="text-3xl font-bold mb-4">Ready for Professional Hydro Jetting?</h3>
              <p className="text-xl mb-6 text-trust-light">
                See your exact price above • No hidden fees • Master plumber owned • 293+ verified reviews
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                
                <a 
                  href="/sewer-cleaning-pricing" 
                  className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-10 py-5 rounded-full text-xl font-semibold inline-block transition-all duration-300 w-full sm:w-auto"
                >
                  🔧 See Other Sewer Cleaning Services
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HydroJettingPricing;
